import { AxiosInstance } from 'axios';
import { Campaign, CampaignInfo } from '../types/Campaign';
import ApiClient from './ApiClient';
import { SubscriptionEligibility } from '../types/Subscription';

// API URL from environment variables
const API_URL = process.env.REACT_APP_CAMPAIGN_API_URL ?? "";

// Create an instance of ApiClient for the campaign service
const apiClientInstance = new ApiClient(API_URL);
const apiClient = apiClientInstance.getClient();

export class CampaignService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = apiClient;
  }

  public async getCampaigns(organizationId: string): Promise<CampaignInfo[]> {
    try {
      const response = await this.apiClient.get<any[]>(`/${organizationId}/campaigns`);
      // Transform the response to return only id and name and map snake_case to camelCase
      return response.data.map(campaign => ({
        id: campaign.id,
        name: campaign.name || '',
        organizationId: campaign.organization_id || organizationId // Map from snake_case to camelCase, fallback to passed organizationId
      }));
    } catch (error) {
      console.error(`CampaignService - Error fetching campaigns for organization ${organizationId}:`, error);
      return []; // Return an empty array in case of error
    }
  }

  public async getSubscriptionEligibility(organizationId: string): Promise<SubscriptionEligibility> {
    try {
      const response = await this.apiClient.get<any>(`/${organizationId}/eligibility`);
      return response.data;
    } catch (error) {
      console.error(`CampaignService - Error fetching subscription eligibility:`, error);
      return { can_create_campaign: false };
    }
  }

  public async getCampaignById(organizationId: string, id: string): Promise<Campaign | null> {
    try {
      console.log(`CampaignService - Fetching campaign with id ${id} for organization ${organizationId}`);
      const url = `/${organizationId}/campaigns/${id}`;
      console.log(`CampaignService - API URL: ${url}`);

      const response = await this.apiClient.get<any>(url);
      console.log(`CampaignService - Response received:`, response.data);

      // Handle nested response structure
      if (response.data && response.data.id) {
        // Always include the organization_id in the result
        const result = {
          id: response.data.id,
          organization_id: organizationId, // Add the organization_id to the campaign object
          ...response.data
        };

        // If response has 'details' field, ensure it's in the correct format
        if (response.data.details) {
          result.details = response.data.details;
        }

        console.log(`CampaignService - Returning processed data:`, result);
        return result;
      }

      console.log(`CampaignService - No valid response data found`);
      return null;
    } catch (error) {
      console.error(`CampaignService - Error fetching campaign:`, error);
      return null; // Return null in case of error
    }
  }

  public async createCampaign(organizationId: string, campaign: Partial<Campaign>): Promise<Campaign | null> {
    try {
      const response = await this.apiClient.post<any>(`/${organizationId}/campaigns`, campaign);

      // Handle nested response structure
      if (response.data && response.data.id) {
        // If response has 'details' field, merge it with the id at the top level
        if (response.data.details) {
          return {
            id: response.data.id,
            ...response.data.details
          };
        }
        // If response has a flat structure, return it directly
        return response.data;
      }

      return null;
    } catch (error) {
      console.error(`Error creating campaign for organization ${organizationId}:`, error);
      return null;
    }
  }

  public async updateCampaign(organizationId: string, id: string, campaign: Partial<Campaign>): Promise<Campaign | null> {
    try {
      let dataToUpdate: any;

      // Determine if we're updating a full Campaign or just CampaignDetails
      if ('details' in campaign || 'dates' in campaign) {
        // We're updating a Campaign object with nested properties
        dataToUpdate = campaign;
      } else {
        // We're updating just the CampaignDetails
        dataToUpdate = {
          details: campaign
        };
      }

      console.log('Sending update to API:', dataToUpdate);
      // Use PATCH instead of PUT for partial updates
      const response = await this.apiClient.patch<Campaign>(`/${organizationId}/campaigns/${id}`, dataToUpdate);

      if (response.data && response.data.id) {
        return response.data;
      }

      return null;
    } catch (error) {
      console.error(`Error updating campaign with id ${id} for organization ${organizationId}:`, error);
      return null;
    }
  }
}

export default CampaignService;
